'use client';

import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import type { CommentSource, CommentsSort } from '@/lib/api/comments';
import { useAuthContext } from '@/lib/auth/AuthProvider';

interface CommentsContextValue {
  source: CommentSource;
  sort: CommentsSort;
  setSort: (sort: CommentsSort) => void;
  onlyHolders: boolean;
  setOnlyHolders: (value: boolean) => void;
  onlyMine: boolean;
  setOnlyMine: (value: boolean) => void;
}

const CommentsContext = createContext<CommentsContextValue | undefined>(undefined);

interface CommentsProviderProps {
  children: ReactNode;
  source: CommentSource;
  initialSort?: CommentsSort;
  initialOnlyHolders?: boolean;
  initialOnlyMine?: boolean;
}

export function CommentsProvider({
  children,
  source,
  initialSort = 'createdAt:desc',
  initialOnlyHolders = false,
  initialOnlyMine = false,
}: CommentsProviderProps) {
  const [sort, setSort] = useState<CommentsSort>(initialSort);
  const [onlyHolders, setOnlyHolders] = useState(initialOnlyHolders);
  const [onlyMine, setOnlyMine] = useState(initialOnlyMine);
  const { isLoading: isAuthLoading, isAuthenticated } = useAuthContext();

  useEffect(() => {
    if (!isAuthLoading && !isAuthenticated) {
      setOnlyMine(false);
    }
  }, [isAuthLoading, isAuthenticated]);

  const value = useMemo<CommentsContextValue>(
    () => ({ source, sort, setSort, onlyHolders, setOnlyHolders, onlyMine, setOnlyMine }),
    [source, sort, onlyHolders, onlyMine],
  );

  return <CommentsContext.Provider value={value}>{children}</CommentsContext.Provider>;
}

export function useCommentsContext(): CommentsContextValue {
  const ctx = useContext(CommentsContext);
  if (!ctx) {
    throw new Error('useCommentsContext must be used within CommentsProvider');
  }
  return ctx;
}
